import type { SwiftsellMaterialOption } from "../../api/swiftsell";
import type { LegOption } from "./legOptions";

export type LineItem = {
  sku: string;
  fabric: SwiftsellMaterialOption;
  quantity: number;
  leg: LegOption | null;
};

/**
 * Identity of a line item for merging purposes. Two items merge only when they share the
 * same SKU, the same fabric, and the same leg.
 */
export const lineItemKey = (item: { sku: string; fabric: { sku: string }; leg: { code: string } | null }) =>
  `${item.sku}__${item.fabric.sku}__${item.leg?.code ?? ""}`;

/**
 * Collapse a list of line items so that items with the same key (SKU + fabric + leg) become a
 * single entry whose quantity is the sum of the merged items. Order of first appearance is
 * preserved; the fabric/leg of the first occurrence is kept.
 */
export const mergeLineItems = (items: LineItem[]): LineItem[] => {
  const byKey = new Map<string, LineItem>();

  items.forEach((item) => {
    const key = lineItemKey(item);
    const existing = byKey.get(key);

    if (existing) {
      byKey.set(key, { ...existing, quantity: existing.quantity + item.quantity });
    } else {
      byKey.set(key, { ...item });
    }
  });

  return Array.from(byKey.values());
};
